import * as React from 'react';
import {
  AutomationAction,
  AUTOMATION_ACTION_TYPE,
} from '../../../../MedicareProAPI/Entities/Automation/AutomationAction';
import {
  AUTOMATION_ACTION_TYPE_LABELS,
  USER_ACTIVITY_PRIORITY_TYPE_LABELS,
  THANKSIO_MAIL_TYPE_LABELS,
  THANKSIO_POSTCARD_SIZE_LABELS,
  DIRECTMAILMANAGER_POSTCARD_SIZE_LABELS,
} from '../../../Language/en';
import { WebAPI } from '../../../../MedicareProAPI/API/WebAPI';

import StyledModal from '../../../Components/StyledModal';
import { UserPersonalTokenRepository } from '../../../../MedicareProAPI/Repositories/User/UserPersonalTokenRepository';
import type { UserPersonalToken } from '../../../../MedicareProAPI/Entities/User/UserPersonalToken';

export const DIRECTMAILMANAGER_POSTCARD_SIZE = {
  FOURBYSIX: '4x6',
  SIXBYNINE: '6x9',
} as const;

export type DIRECTMAILMANAGER_POSTCARD_SIZE =
  (typeof DIRECTMAILMANAGER_POSTCARD_SIZE)[keyof typeof DIRECTMAILMANAGER_POSTCARD_SIZE];

export interface DirectMailManagerTemplateProps {
  postcardSize?: DIRECTMAILMANAGER_POSTCARD_SIZE;
  templateFrontId?: string;
  templateBackId?: string;
  name?: string;
  sendDate?: string;
}

export interface DirectMailManagerTemplateSelectProps {
  userAccessToken: string;
  userId: number;

  saveButtonLabel?: string;
  onSave?: (templateData: DirectMailManagerTemplateProps) => void;

  templateData: DirectMailManagerTemplateProps;
}

export const DirectMailManagerTemplateSelect = (
  props: DirectMailManagerTemplateSelectProps
): React.JSX.Element => {
  const [saveButtonLabel, setSaveButtonLabel] = React.useState<string>(
    props.saveButtonLabel || 'Save Changes'
  );

  const [templateData, setTemplateData] = React.useState<DirectMailManagerTemplateProps>(
    props.templateData || {}
  );
  const [templateLiveData, setTemplateLiveData] = React.useState<DirectMailManagerTemplateProps>(
    props.templateData || {}
  );
  const [templateLiveDataTemplateFront, setTemplateLiveDataTemplateFront] = React.useState<any>();

  const [templates, setTemplates] = React.useState<any>();
  const [templatesLoading, setTemplatesLoading] = React.useState<boolean>(true);

  const [filteredTemplatesFront, setFilteredTemplatesFront] = React.useState<any>();
  const [filteredTemplatesBack, setFilteredTemplatesBack] = React.useState<any>();

  const [userPersonalToken, setUserPersonalToken] = React.useState<UserPersonalToken>();

  const [showModal, setShowModal] = React.useState<boolean>(false);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  React.useEffect(() => {
    // Message tmeplates
    webAPI.getUserIntegrationDirectMailManagerArtworks(props.userId).then((response) => {
      setTemplates(response.data);
      setTemplatesLoading(false);
    });

    if (templateData.postcardSize === undefined) {
      handlePostcardSizeChange(DIRECTMAILMANAGER_POSTCARD_SIZE.FOURBYSIX);
    }
  }, []);

  React.useEffect(() => {
    if (templatesLoading === false) {
      let newFilteredTemplates = [];

      if (templates.length > 0) {
        newFilteredTemplates = templates.filter(
          (template) => template.size === templateData.postcardSize
        );
      }

      setFilteredTemplatesFront(newFilteredTemplates.filter((item) => item.side === 'front'));
      setFilteredTemplatesBack(newFilteredTemplates.filter((item) => item.side === 'back'));
    }
  }, [templateData.postcardSize, templatesLoading]);

  const displayModal = () => {
    setShowModal(true);
  };

  const hideModal = () => {
    setShowModal(false);
  };

  const handlePostcardSizeChange = (value?: DIRECTMAILMANAGER_POSTCARD_SIZE) => {
    handleTemplateDataChange({ ...templateData, postcardSize: value });
  };

  const handleTemplateFrontIdChange = (value?: string) => {
    handleTemplateDataChange({ ...templateData, templateFrontId: value });
  };

  const handleTemplateBackIdChange = (value?: string) => {
    handleTemplateDataChange({ ...templateData, templateBackId: value });
  };

  const handleSendDateChange = (value?: string) => {
    handleTemplateDataChange({ ...templateData, sendDate: value });
  };

  const handleNameChange = (value?: string) => {
    handleTemplateDataChange({ ...templateData, name: value });
  };

  const handleTemplateDataChange = (newTemplateData: DirectMailManagerTemplateProps) => {
    setTemplateData(newTemplateData);
  };

  React.useEffect(() => {
    if (templateLiveData) {
      if (templateLiveData.templateFrontId) {
        webAPI
          .getUserIntegrationDirectMailManagerArtwork(
            props.userId,
            templateLiveData.templateFrontId
          )
          .then((templateResponse) => {
            setTemplateLiveDataTemplateFront(templateResponse);
          });
      }
    }
  }, [templateLiveData]);

  return (
    <>
      <StyledModal
        size="xl"
        title="Direct Mail Manager"
        footerCloseButton={true}
        footerButtons={
          <div
            onClick={(e) => {
              if (props.onSave) {
                props.onSave(templateData);
                setTemplateLiveData({ ...templateData });
              }

              hideModal();
            }}
            className="btn btn-success"
          >
            {saveButtonLabel}
          </div>
        }
        show={showModal}
        onClose={hideModal}
      >
        <div className="row">
          <div className="col-12 col-lg-3 mb-2 border-right ">
            <div>
              {DIRECTMAILMANAGER_POSTCARD_SIZE_LABELS.map((item) => {
                const icon = <i className="fas fa-mailbox"></i>;
                const selected = templateData.postcardSize === item.value;

                const classList = [
                  'btn',
                  'btn-sm',
                  'border',
                  'border-secondary',
                  'mb-2',
                  'w-100',
                  'p-2',
                ];

                if (selected) {
                  classList.push('btn-info');
                }

                return (
                  <div
                    onClick={(e) => handlePostcardSizeChange(item.value)}
                    className={classList.join(' ')}
                  >
                    <div>{icon}</div>
                    <b>{item.label}</b>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="col-12 col-lg-9" /*style="max-height:450px; overflow-y:scroll;"*/>
            <div className="mt-3">
              <div className="label font-weight-bold mt-3 mb-1 calMess">Postcard Name*</div>
              <input
                type="text"
                className="form-control form-control-sm"
                value={templateData.name}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>
            {/*
                    <div className="mt-3">
                        <div className="label font-weight-bold mt-3 mb-1 calMess">Send date</div>
                        <div className="input-group date" data-provide="datepicker">
                            <input type="text" className="form-control form-control-sm dmmSendDate needsSlashes" onChange={e => handleSendDateChange(e.target.value)} value={templateData.sendDate}/>
                            <div className="input-group-addon"><span className="glyphicon glyphicon-th"></span></div>
                        </div>
                        <span className="calMess"><i>*postcard will send out immediately if no date is selected</i></span>
                    </div>
                    */}
            <div className="mt-3">
              <label className="label">
                <b>Select front template</b>
              </label>
              {templatesLoading === true && (
                <div className="alert small alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                </div>
              )}
              {templatesLoading === false &&
                filteredTemplatesFront &&
                filteredTemplatesFront.length === 0 && (
                  <div className="alert small alert-info" role="alert">
                    No templates to show
                  </div>
                )}
              {filteredTemplatesFront && filteredTemplatesFront.length > 0 && (
                <div className="row">
                  {filteredTemplatesFront.map((template) => {
                    const templateCheckClassList = [
                      'fas',
                      'fa-check-circle',
                      'text-success',
                      'float-right',
                      'imageTemplateSelectedCheck',
                    ];

                    const selected = template.id === templateData.templateFrontId;

                    if (!selected) {
                      templateCheckClassList.push('d-none');
                    }

                    return (
                      <div
                        onClick={(e) => handleTemplateFrontIdChange(template.id)}
                        className="ml-3 col-12 col-lg-4 border border-secondary rounded mb-2 mt-2 text-center cp  p-2 thanksio-image-template"
                      >
                        <div className="font-weight-bold mb-2">
                          {template.name}
                          <i className={templateCheckClassList.join(' ')}></i>
                        </div>
                        <img src={template.thumbnails.medium} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="mt-3">
              <label className="label">
                <b>Select back template</b>
              </label>
              {templatesLoading === true && (
                <div className="alert small alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                </div>
              )}
              {templatesLoading === false &&
                filteredTemplatesBack &&
                filteredTemplatesBack.length === 0 && (
                  <div className="alert small alert-info" role="alert">
                    No templates to show
                  </div>
                )}
              {filteredTemplatesBack && filteredTemplatesBack.length > 0 && (
                <div className="row">
                  {filteredTemplatesBack.map((template) => {
                    const templateCheckClassList = [
                      'fas',
                      'fa-check-circle',
                      'text-success',
                      'float-right',
                      'imageTemplateSelectedCheck',
                    ];
                    const selected = template.id === templateData.templateBackId;

                    if (!selected) {
                      templateCheckClassList.push('d-none');
                    }

                    return (
                      <div
                        onClick={(e) => handleTemplateBackIdChange(template.id)}
                        className="ml-3 col-12 col-lg-4 border border-secondary rounded mb-2 mt-2 text-center cp  p-2 thanksio-image-template"
                      >
                        <div className="font-weight-bold mb-2">
                          {template.name}
                          <i className={templateCheckClassList.join(' ')}></i>
                        </div>
                        <img src={template.thumbnails.medium} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </StyledModal>

      <div>
        {templateLiveDataTemplateFront && (
          <img
            className="template-preview-image"
            src={templateLiveDataTemplateFront.thumbnails.medium}
          />
        )}
        <div onClick={displayModal} className="btn btn-sm border border-primary">
          {templateLiveDataTemplateFront ? 'Change Template' : 'Choose Template'}
        </div>
      </div>

      <small>
        <i className="far fa-question-circle mr-1 "></i>What template do you want to use?
      </small>
    </>
  );
};
